import { isiOS } from "./ua-utils";
import showToast from "./showToast";
import { calculateMatchingScore, getWordSearchScore } from "./search";
import { escapeHTML } from "./escape";

interface Dimensions {
    width: number;
    height: number;
}

interface GetScaledDimensionsParams extends Dimensions {
    containerWidth: number;
}

interface HighlightOptions {
    caseInsensitive?: boolean; // if true, comparison will be made in caseInsensitive format
    excludeZeroScore?: boolean; // if true, text will not be highlighted if matching score = 0
    enableWordSearch?: boolean;
    highlightClass?: string;
}

/**
 * A bug in iOS and iPadOS Safari prevents selecting text with {@link HTMLTextAreaElement.select}, and it only works
 * when the selection is made via {@link HTMLTextAreaElement.setSelectionRange}.
 */
function selectText(textarea: HTMLTextAreaElement): void {
    if (isiOS()) {
        const range = document.createRange();
        range.selectNodeContents(textarea);

        const selection = window.getSelection()!;
        selection.removeAllRanges();
        selection.addRange(range);
        textarea.setSelectionRange(0, textarea.value.length);
    } else {
        textarea.select();
    }
}

/**
 * Copies text to clipboard without using navigator api
 */
function copyTextLegacy(text: string): Promise<boolean> {
    const textarea = document.createElement("textarea");
    textarea.classList.add("copyContainer");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.focus();
    selectText(textarea);

    let success = false;
    try {
        success = document.execCommand("copy");
    } finally {
        textarea.remove();
    }

    return Promise.resolve(success);
}

/**
 * Copies text to clipboard
 */
function copyText(text: string) {
    return navigator.clipboard.writeText(text)
        .then(() => true)
        .catch(() => false);
}

function copyContent({ content, toastContainer, successText }: {
    content: string;
    toastContainer?: Element | null;
    successText?: string;
}) {
    const copyFunction = navigator.clipboard ? copyText : copyTextLegacy;

    copyFunction(content).then(success => {
        if (success && toastContainer) {
            showToast({
                text: successText ?? "Copied to clipboard!",
                container: toastContainer,
                source: content
            });
        }
    });
}

function getScaledDimensions({ width, height, containerWidth }: GetScaledDimensionsParams): Dimensions {
    if (width <= containerWidth) {
        return { width, height };
    }

    const scaledWidth = containerWidth;
    const scaledHeight = (scaledWidth * height) / width;

    return { width: scaledWidth, height: scaledHeight };
}

/**
 * Selects content of given element
 */
function selectContent(element: Element) {
    const selection = getSelection();
    if (!selection) {
        return;
    }
    selection.removeAllRanges();
    const range = document.createRange();
    range.selectNodeContents(element);
    selection.addRange(range);
}

/**
 * Deselects all selected content on the screen
 */
function deselectContent() {
    const selection = getSelection();
    if (!selection) {
        return;
    }
    selection.removeAllRanges();
}

/**
 * Highlight matched parts with span.highlight
 *
 * @param target text to be highlighted
 * @param term search term
 * @param highlightOptions options
 *
 * @returns highlighted text in escaped HTML form
 */
function highlightMatchedParts(
    target: string,
    term: string,
    options: HighlightOptions = {}) {
    const {
        caseInsensitive,
        excludeZeroScore,
        enableWordSearch,
        highlightClass = "highlight"
    } = options;
    const normalizedTarget = caseInsensitive ? target.toLowerCase() : target;
    const normalizedTerm = caseInsensitive ? term.toLowerCase() : term;
    let index = 0;
    let html = "";

    if ((
        excludeZeroScore &&
        !calculateMatchingScore({ searchTerm: normalizedTerm, target: normalizedTarget, enableWordSearch })
    ) || (
        enableWordSearch &&
        !getWordSearchScore(normalizedTarget, normalizedTerm)
    )) {
        return escapeHTML(target);
    }

    const fullMatchIndex = normalizedTarget.indexOf(normalizedTerm);
    if (fullMatchIndex > -1) {
        html += escapeHTML(target.substring(0, fullMatchIndex));
        html += `<span class="${highlightClass}">${escapeHTML(target.substr(fullMatchIndex, normalizedTerm.length))}</span>`;
        html += escapeHTML(target.substring(fullMatchIndex + normalizedTerm.length));
    } else {
        for (const termChar of normalizedTerm) {
            const foundIndex = normalizedTarget.indexOf(termChar, index);
            if (foundIndex === -1) {
                break;
            }

            html += escapeHTML(target.substring(index, foundIndex));
            html += `<span class="${highlightClass}">${escapeHTML(target.substr(foundIndex, termChar.length))}</span>`;
            index = foundIndex + termChar.length;
        }

        html += escapeHTML(target.substring(index));
    }

    return html;
}

function clearNativeSelection() {
    // Add setTimeout to make sure that the 'removeAllRanges' is called after the native selection
    setTimeout(() => {
        window.getSelection()?.removeAllRanges();
    });
}

export {
    copyContent,
    getScaledDimensions,
    selectContent,
    deselectContent,
    highlightMatchedParts,
    clearNativeSelection
};
